import React, { useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { AlertCircle, Home, LogOut } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Link, Redirect } from 'expo-router'
import * as Sentry from '@sentry/react-native'
import Head from 'expo-router/head'

import {
  subscriptionKeys,
  useSubscriptionDetailsQueryFn,
  useSubscriptions,
} from '@/hooks/useSubscription'
import { useClientSession } from '@/hooks/useClientSession'
import { useLogout } from '@/hooks/useLogout'
import { SubscriptionAvatar } from '@/components/SubscriptionAvatar'
import { GettingStartedCardPrivate } from '@/components/GettingStartedCardPrivate'
import { SubscriptionList } from '@/components/SubscriptionList'
import { SubLoadingState } from '@/components/SubLoadingState'
import { Footer } from '@/components/Footer'
import { CustomerInfoCard } from '@/components/CustomerInfoCard'

export default function ConfigureSubScreen() {
  const sessionQuery = useClientSession()
  const userId = sessionQuery.data?.user?.id
  const subscriptionsQuery = useSubscriptions(userId)
  const logout = useLogout()

  const queryClient = useQueryClient()
  const firstSub = subscriptionsQuery.data?.[0]?.id
  const customerId = subscriptionsQuery.data?.[0]?.user_id

  useEffect(() => {
    if (firstSub) {
      queryClient
        .prefetchQuery({
          queryKey: subscriptionKeys.useSubscriptionDetails(firstSub),
          queryFn: () => useSubscriptionDetailsQueryFn(firstSub),
        })
        .catch((e) => Sentry.captureException(e))
    }
  }, [firstSub])

  if (sessionQuery.isSuccess && sessionQuery.data === null) {
    const paramsString = new URLSearchParams(window.location.search).toString()
    const href = `/auth/login?${paramsString}` as const
    return <Redirect href={href} />
  }

  if (sessionQuery.isPending || subscriptionsQuery.isPending) {
    return <SubLoadingState />
  }

  if (sessionQuery.isError || subscriptionsQuery.isError) {
    const error = sessionQuery.error || subscriptionsQuery.error

    return (
      <Card className="w-full max-w-md mx-auto border-destructive bg-card">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <AlertCircle className="h-8 w-8 text-destructive mb-2" />
          <p className="text-destructive font-medium text-center">Error loading subscription</p>
          <p className="text-sm text-muted-foreground mt-1 text-center">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Link href="/auth/login" asChild>
            <Button variant="outline" className="transform duration-200 mt-4">
              Go to login
            </Button>
          </Link>
        </CardContent>
      </Card>
    )
  }

  const email = sessionQuery.data?.user.email ?? ''

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Head>
        <title>Subscription Dashboard - React Native Google Sign In</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      {/* Fixed header */}
      <header className="sticky top-0 z-10 bg-background border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Title */}
            <div className="flex items-center justify-center sm:justify-start gap-3">
              <Link href="/">
                <span className="text-foreground hover:text-primary">
                  <Home className="h-5 w-5" />
                </span>
              </Link>
              <h1 className="text-xl sm:text-2xl font-bold">Subscription Dashboard</h1>
            </div>

            {/* User info and controls */}
            <div className="flex items-center justify-between sm:justify-end gap-4">
              <SubscriptionAvatar publicName={email} email={email} />

              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Scrollable content */}
      <div className="flex-1 flex flex-col overflow-auto">
        <main className="flex-1">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="grid gap-6 md:grid-cols-2 md:gap-8">
              {/* Main Content */}
              <div className="space-y-6">
                {/* Getting Started Card */}
                <GettingStartedCardPrivate />
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <CustomerInfoCard customerId={customerId} email={email} subscription={subscriptionsQuery.data?.[0]} />
                <SubscriptionList subscriptionsQuery={subscriptionsQuery} />
              </div>
            </div>
          </div>
        </main>
        <div className="border-t sm:mt-auto">
          <Footer showCopyRight={false} />
        </div>
      </div>
    </div>
  )
}
